// Shapes + shared helpers for the inquiry draft composer. These ARE the contract
// the hooks in inquiryDraftApi.ts expose — the backend is real; nothing here mocks it.

import {
  type CatalogLanguage,
  getCatalog,
} from "@/hooks/useTranslation/catalogs"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { InquirySettingsStatus } from "../../../../api/supplierInquiriesApi"
import type { DraftArticle } from "./AddArticleDialog"

export type Stage = "first" | "reminder"

// The questionnaire is delivered as a full HTML document by the backend. We only
// support EN/DE renderings of it.
export type Lang = "en" | "de"

// How the questionnaire is attached to the sent email: the branded PDF or the fill-in
// Excel compliance workbook. One per inquiry, frozen on send (like `language`).
// pdf_and_excel = the two-attachment package: short Declaration-of-Conformity PDF
// (backend variant declaration_v2) plus the Excel workbook in ONE email.
// none = internal-only, no questionnaire attached at all: a declaration-review
// Rückfrage (create_declaration_inquiry) stores it and the backend keeps it standing
// on reminders: the ordinary composer never sets it (backend refuses that write),
// so the only path INTO this value is the seeded/persisted read.
export type AttachmentFormat = "pdf" | "excel" | "pdf_and_excel" | "none"

export interface InquiryEmailCopyCell {
  subject: string
  body: string
}

export type InquiryEmailCopy = Record<Stage, Record<Lang, InquiryEmailCopyCell>>

export interface InquiryDraftItem {
  id: string // inquiry id (from URL)
  reference?: string | null // Supplier-Inquiry ref; null until assigned on send (shown in the subject's [Ref:] chip)
  status?: string // lifecycle status (draft | waiting | …); only drafts can be sent
  supplierId: string // resolves against the real master-data articles endpoint
  supplierNumber: string
  supplierName: string
  contactEmails: string[] // To recipients (real contact comes from the inquiry-details API)
  contactEmailsCc?: string[] // Cc recipients (persisted; seeded back on load)
  frameworks: string[]
  articlesCount: number
  articles: DraftArticle[] // articles the backend pre-filled into the questionnaire table
  questionnaire: Record<Stage, Record<Lang, string>> // backend-rendered HTML per stage + language, article table already filled
  emailCopy: InquiryEmailCopy // resolved subject/body copy per stage + language
  language?: Lang | null // persisted questionnaire language (defaults to DE when absent)
  attachmentFormat?: AttachmentFormat // persisted send format (defaults to pdf)
  dueAt?: string | null // persisted response deadline (ISO); absent on a fresh draft
  // Whether the org's template settings resolved for this read (drives the composer's
  // "set up templates" nudge / "couldn't load — retry" banner).
  settingsStatus?: InquirySettingsStatus
  // Settings > Regulations narrowing, resolved server-side for the persisted format:
  // the framework keys the documents ask about (null = no selection, full documents)
  // and NO_INQUIRY_REGULATIONS when that format covers none of them.
  askedRegulations?: string[] | null
  // What the inquiry is meant to ask (its pick, else Settings > Regelwerke); keys not in
  // askedRegulations have no section in this format.
  regulationKeys?: string[] | null
  regulationScopeError?: string | null
  // "explicit" once someone picked the regulations, "default" while it follows Settings.
  scopeSource?: "default" | "explicit" | null
}

// Variable tokens offered as insertable pills, localized so a German editor sees German
// tokens ({{lieferant}}/{{frist}}). Both language sets resolve on send/preview (backend
// aliases), so switching language never breaks an already-inserted token.
export const INQUIRY_VARS_BY_LANG: Record<Lang, string[]> = {
  en: ["{{supplier_name}}", "{{deadline}}", "{{regulations}}"],
  de: ["{{lieferant}}", "{{frist}}", "{{regelwerke}}"],
}

// i18n keys describing what each variable resolves to — shown in the pill's tooltip.
// Both the EN and DE token for a field map to the same tooltip.
export const INQUIRY_VAR_TOOLTIP_KEYS: Record<string, TranslationKeys> = {
  "{{supplier_name}}": "MC_INQ_DRAFT_VAR_TOOLTIP_SUPPLIER_NAME",
  "{{lieferant}}": "MC_INQ_DRAFT_VAR_TOOLTIP_SUPPLIER_NAME",
  "{{deadline}}": "MC_INQ_DRAFT_VAR_TOOLTIP_DEADLINE",
  "{{frist}}": "MC_INQ_DRAFT_VAR_TOOLTIP_DEADLINE",
  "{{regulations}}": "MC_INQ_DRAFT_VAR_TOOLTIP_REGULATIONS",
  "{{regelwerke}}": "MC_INQ_DRAFT_VAR_TOOLTIP_REGULATIONS",
}

// LAST-RESORT response window when the inquiry has no persisted due date AND the org's
// settings have not loaded: the deadline becomes today + N days. Mirrors the backend's
// own INQUIRY_DUE_DEFAULT_DAYS so composer and server agree on the fallback. The org's
// configured window (`defaultDueDays`, Erinnerungsrhythmus) OUTRANKS this everywhere it
// is known — see `deriveInquiryDue`.
export const INQUIRY_DUE_DEFAULT_DAYS = 30

// The composer stores the deadline as a plain "YYYY-MM-DD" calendar date — a
// stable, machine-parseable format — and only formats it for display where
// needed. Never store/round-trip a locale-formatted string (e.g. "Jun 30,
// 2026"): `new Date()` parsing of non-ISO strings is implementation-defined
// and varies across browsers, and it invites timezone/date-shift bugs when
// later converting to an instant for the API.
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/

function pad2(n: number): string {
  return String(n).padStart(2, "0")
}

/** Local calendar date -> "YYYY-MM-DD" using LOCAL getters (no UTC shift). */
export function toIsoDateString(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

/**
 * "YYYY-MM-DD" -> a Date at LOCAL midnight for that calendar day (for the
 * Calendar's `selected` prop / display formatting). Returns undefined for an
 * empty/malformed value rather than falling back to `new Date(iso)`, which
 * would parse the date as UTC and can render as the previous day locally.
 */
export function parseIsoDate(iso: string): Date | undefined {
  const m = ISO_DATE_RE.exec(iso)
  if (!m) return undefined
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/** Display label for the deadline picker (e.g. "Jun 30, 2026"). */
export function formatInquiryDue(iso: string, locale = "en-US"): string {
  const date = parseIsoDate(iso)
  if (!date) return iso
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/**
 * Converts the composer's "YYYY-MM-DD" deadline into a UTC-midnight instant
 * for the send payload, via plain string formatting — never through a local
 * Date + `toISOString()` round-trip, which would shift the date backward for
 * any timezone behind UTC (local midnight -> an earlier UTC instant on the
 * previous day).
 */
export function isoDateToUtcMidnight(iso: string): string | undefined {
  const m = ISO_DATE_RE.exec(iso)
  if (!m) return undefined
  return `${m[1]}-${m[2]}-${m[3]}T00:00:00.000Z`
}

/**
 * The deadline shown in the composer: the persisted due date when still ahead, else
 * today + the org's response window. A stale draft's seeded date has LAPSED, and sending
 * an expired deadline trips the first auto-reminder at once. Returns "YYYY-MM-DD".
 *
 * `dueDays` is the org's central setting; omitted (settings still loading, or a caller
 * that has none) it falls back to the shipped default, which is what this always used.
 * The backend seeds a real `due_at` at draft creation from the SAME org setting, so this
 * fallback is the stale-draft path, not the normal one.
 */
export function deriveInquiryDue(
  dueAt: string | null | undefined,
  dueDays: number = INQUIRY_DUE_DEFAULT_DAYS,
): string {
  // The backend's persisted value already carries its calendar date in its
  // first 10 characters (date-only or full ISO datetime) — read it directly
  // rather than round-tripping through a local Date, which would re-interpret
  // it in the browser's timezone and can shift the day.
  if (dueAt) {
    const m = ISO_DATE_RE.exec(dueAt)
    // "YYYY-MM-DD" strings compare lexicographically, so no Date detour is needed.
    if (m && `${m[1]}-${m[2]}-${m[3]}` >= toIsoDateString(new Date())) {
      return `${m[1]}-${m[2]}-${m[3]}`
    }
  }
  const fallback = new Date()
  fallback.setDate(fallback.getDate() + dueDays)
  return toIsoDateString(fallback)
}

/**
 * The backend V2 questionnaire repeats the article list across FIVE annex tables,
 * each with its own columns (Annex A + B.1–B.4). Every row leads with position +
 * article number; Annex A also shows the description; the rest are empty
 * supplier-fill cells. The backend tags each table body with a stable id and fills
 * it server-side; when the user edits the article list the composer re-renders just
 * these bodies for an instant preview (swapInquiryArticleRows), leaving the rest of
 * the backend HTML untouched. Keep in sync with the renderer's `_ANNEX_TABLES`.
 */
const ANNEX_TABLES: {
  id: string
  includeDescription: boolean
  fill: number
}[] = [
  { id: "mc-inq-annex-a", includeDescription: true, fill: 2 },
  { id: "mc-inq-b1", includeDescription: false, fill: 4 },
  { id: "mc-inq-b2", includeDescription: false, fill: 6 },
  { id: "mc-inq-b3", includeDescription: false, fill: 6 },
  { id: "mc-inq-b4", includeDescription: false, fill: 5 },
]

// Org-level branding the backend injects into the questionnaire (settings preview).
export const SAMPLE_LOGO_URL =
  "https://1000logos.net/wp-content/uploads/2021/11/Nike-Logo.png"

/** Catalogs the questionnaire preview reads, whatever the UI language: the
 *  inquiry draft route renders under
 *  `<CatalogGate languages={INQUIRY_DOCUMENT_CATALOGS}>`. */
export const INQUIRY_DOCUMENT_CATALOGS: readonly CatalogLanguage[] = [
  "EN",
  "DE",
]

// "No articles yet" copy for the article table body, per (document) language.
// Sourced from the canonical translation files rather than a parallel literal
// dictionary — this is the questionnaire's rendering language, which is
// independent of the app's own UI language (EN/DE/JA), so it can't go through
// the currentLanguage-bound translate() hook.
function emptyArticlesText(lang: Lang): string {
  const catalog = getCatalog(lang === "en" ? "EN" : "DE")
  return catalog?.MC_INQ_DRAFT_ANNEX_EMPTY || "MC_INQ_DRAFT_ANNEX_EMPTY"
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  )
}

/**
 * Rows for ONE annex table body: position + article number, then (Annex A only) the
 * description, then `fill` empty supplier-fill cells. Markup mirrors the backend
 * renderer's `_render_annex_rows` so a client-side swap is indistinguishable from a
 * fresh backend render.
 */
function renderAnnexRows(
  articles: DraftArticle[],
  lang: Lang,
  includeDescription: boolean,
  fill: number,
): string {
  const total = 2 + (includeDescription ? 1 : 0) + fill
  if (articles.length === 0) {
    return `<tr><td class="empty" colspan="${total}">${emptyArticlesText(lang)}</td></tr>`
  }
  return articles
    .map((a, i) => {
      const cells = [
        `<td class="pos">${i + 1}</td>`,
        `<td class="mono">${escapeHtml(a.articleNumber)}</td>`,
      ]
      if (includeDescription) cells.push(`<td>${escapeHtml(a.name)}</td>`)
      for (let k = 0; k < fill; k++) cells.push(`<td class="fill"></td>`)
      return `<tr>${cells.join("")}</tr>`
    })
    .join("")
}

/**
 * Swaps the article rows inside an already-rendered questionnaire (the HTML the
 * backend returned) for EACH of the five annex tables, located by their <tbody id>.
 * Everything outside those bodies is left exactly as the backend produced it. Call
 * this only when the user has changed the article list (instant preview); the
 * backend re-renders identical rows on the next load.
 */
export function swapInquiryArticleRows(
  html: string,
  articles: DraftArticle[],
  lang: Lang,
): string {
  return ANNEX_TABLES.reduce((acc, table) => {
    const rows = renderAnnexRows(
      articles,
      lang,
      table.includeDescription,
      table.fill,
    )
    // Replacer fn so `$` sequences in article text (e.g. "Bracket $1") are inserted
    // verbatim rather than treated as replacement patterns.
    return acc.replace(
      new RegExp(`(<tbody id="${table.id}"[^>]*>)[\\s\\S]*?(</tbody>)`),
      (_m, open, close) => `${open}${rows}${close}`,
    )
  }, html)
}
